import React from 'react'
import moment from 'moment'
import capitalize from 'lodash/capitalize'
import { Box, Flex, Text } from '@fundamentals'
import { Card } from '@common/material'
import { TaskTemplateProperties } from '@core/entities/task/TaskTemplateProperties'
import { localizedStrings } from '@core/strings'
import { Section } from '@common/Section'
import { WarningAmber } from '@mui/icons-material'
import { TaskAuditResultMetadata } from '@core/entities/task/TaskAuditResultMetadata'

type TaskPropertiesDiffCardProps = {
  preValues: TaskAuditResultMetadata
  postValues: TaskAuditResultMetadata
}

export const TaskPropertiesDiffCard: React.FC<TaskPropertiesDiffCardProps> = ({
  preValues,
  postValues,
}) => {
  return (
    <Card sx={{ p: 1.5 }} data-test='task-properties-diff-card'>
      {preValues.assigneeUser !== postValues.assigneeUser && (
        <Flex alignItems='flex-start'>
          <Text variant='body2' color='text.primary' sx={{ maxWidth: '40%' }}>
            {preValues.assigneeUser.getFullName()}
          </Text>
          <Text variant='body2' color='text.secondary' mx={2}>
            →
          </Text>
          <Text
            variant='body2'
            color='text.primary'
            sx={{ maxWidth: '40%' }}
            data-test='task-assignee-post-value'
          >
            {postValues.assigneeUser.getFullName()}
          </Text>
        </Flex>
      )}
      {preValues.priority !== postValues.priority && (
        <Section
          labelProps={{ flex: 1 }}
          label={localizedStrings.priority}
          horizontal
        >
          <Flex flex={5} alignItems='flex-start'>
            <Flex alignItems='center'>
              <Box
                height={16}
                width={16}
                borderRadius='50%'
                sx={{
                  backgroundColor: TaskTemplateProperties.getColorForPriority(
                    preValues.priority,
                  ),
                  mr: 1,
                }}
              />
              <Text variant='body2'>{capitalize(preValues.priority)}</Text>
            </Flex>
            <Text variant='body2' color='text.secondary' mx={2}>
              →
            </Text>
            <Flex
              alignItems='center'
              data-test={`task-priority-post-value-${postValues.priority}`}
            >
              <Box
                height={16}
                width={16}
                borderRadius='50%'
                sx={{
                  backgroundColor: TaskTemplateProperties.getColorForPriority(
                    postValues.priority,
                  ),
                  mr: 1,
                }}
              />
              <Text variant='body2'>{capitalize(postValues.priority)}</Text>
            </Flex>
          </Flex>
        </Section>
      )}

      {preValues.dueDate !== postValues.dueDate && (
        <Section
          labelProps={{ flex: 1 }}
          label={localizedStrings.dueDate}
          mt={1}
          horizontal
        >
          <Flex flex={5} justifyContent='flex-start'>
            <Flex justifyContent='flex-start'>
              <Text
                showTooltip
                sx={{
                  color: preValues.isOverdue() ? 'error.main' : 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
                variant='body2'
              >
                {preValues.dueDate
                  ? moment(preValues.dueDate).format('Do MMM YYYY HH:mm')
                  : '-'}
              </Text>
            </Flex>
            <Text variant='body2' color='text.secondary' mx={2}>
              →
            </Text>
            <Flex justifyContent='flex-start'>
              <Text
                showTooltip
                sx={{
                  color: postValues.isOverdue() ? 'error.main' : 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
                variant='body2'
                data-test='task-due-date-post-value'
              >
                {postValues.dueDate
                  ? moment(postValues.dueDate).format('Do MMM YYYY HH:mm')
                  : '-'}
                {postValues.isOverdue() && (
                  <Box
                    component='span'
                    sx={{
                      color: 'error.main',
                      display: 'inline-flex',
                    }}
                  >
                    <WarningAmber />
                  </Box>
                )}
              </Text>
            </Flex>
          </Flex>
        </Section>
      )}

      {postValues.summary !== preValues.summary && (
        <Section
          labelProps={{ flex: 1 }}
          label={localizedStrings.description}
          mt={1}
          horizontal
        >
          <Flex flex={5} justifyContent='flex-start'>
            <Text variant='body2'>{preValues.summary}</Text>
            <Text variant='body2' color='text.secondary' mx={2}>
              →
            </Text>
            <Text variant='body2' data-test='task-summary-post-value'>
              {postValues.summary}
            </Text>
          </Flex>
        </Section>
      )}
    </Card>
  )
}
